/**
 * Utility functions for managing application localStorage
 */

// List of all localStorage keys used by the application
const APP_STORAGE_KEYS = [
  'auth_token',
  'medtriage_user',
  'navigationLayout',
  'mpmmedical_settings',
  'demoMetrics',
  'token', // Legacy key from inventory module
  'auth_user', // Legacy key from inventory module
];

/**
 * Clear all application-related localStorage data
 */
export const clearAllAppStorage = (): void => {
  console.log('🧹 Clearing all application localStorage data...');
  
  APP_STORAGE_KEYS.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed localStorage key: ${key}`);
    }
  });
  
  // Also clear any other keys that might contain our app data
  // This is a safety net for any keys we might have missed
  const allKeys = Object.keys(localStorage);
  allKeys.forEach(key => {
    // Clear any key that contains our app identifiers
    if (key.includes('medtriage') || 
        key.includes('mpm') || 
        key.includes('auth') ||
        key.includes('demo')) {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed additional localStorage key: ${key}`);
    }
  });
  
  console.log('✅ All application localStorage data cleared');
};

/**
 * Check if user data exists in localStorage
 * HIPAA Security: Auth token is now in secure HTTP-only cookie, not localStorage
 */
export const hasValidToken = (): boolean => {
  const user = localStorage.getItem('medtriage_user');
  if (!user) {
    console.log('❌ No user data found in localStorage');
    return false;
  }
  
  try {
    JSON.parse(user); // Validate it's valid JSON
    console.log('✅ Valid user data found');
    return true;
  } catch {
    console.log('❌ Invalid user data format');
    return false;
  }
};

/**
 * Validate auth cookie with backend
 * HIPAA Security: Token is in secure HTTP-only cookie, sent automatically with credentials
 */
export const validateTokenWithBackend = async (): Promise<boolean> => {
  try {
    const response = await fetch('/api/auth/me', {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include' // Send HTTP-only cookie with auth token
    });
    
    if (response.ok) {
      console.log('✅ Auth cookie is valid');
      return true;
    } else if (response.status === 401 || response.status === 403) {
      console.log('❌ Auth cookie is invalid or expired');
      return false;
    } else {
      console.log('⚠️ Auth validation failed with status:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ Error validating auth:', error);
    return false;
  }
};

/**
 * Initialize app with token validation and storage cleanup
 */
export const initializeApp = async (): Promise<{ isValid: boolean; shouldRedirect: boolean }> => {
  console.log('🚀 Initializing application...');
  
  // Check if we have a valid token format
  if (!hasValidToken()) {
    console.log('🔄 No valid token found, clearing storage and redirecting to login');
    clearAllAppStorage();
    return { isValid: false, shouldRedirect: true };
  }
  
  // Validate token with backend
  const isValid = await validateTokenWithBackend();
  
  if (!isValid) {
    console.log('🔄 Token invalid, clearing storage and redirecting to login');
    clearAllAppStorage();
    return { isValid: false, shouldRedirect: true };
  }
  
  console.log('✅ App initialization successful');
  return { isValid: true, shouldRedirect: false };
};
